/**
 * @author: lv.chen
 * @date: 2025/6/16
 * @desc: 指标定义解释，常用于指标hover气泡
 */
import React from 'react';
import classnames from 'classnames';
import { isFunction } from 'lodash-es';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { processRichText } from '@/pages/intelligence/utils/richTextUtils';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { DataSourceBubbleInfo, DATASOURCE_NAME } from '@intelligence/components/DataSourceComponents';

import './index.scss';

/**
 * 指标气泡解释内容
 */
export interface IMetricExplanationTips {
  // 指标定义
  definition: string | React.ReactElement;
  // 数据源
  dataSource: string | React.ReactElement;
  // 数据源名
  dataSourceName?: string | React.ReactElement;
  // 指标名
  metricName?: string | React.ReactElement;
  // 时间粒度
  granularity: string | React.ReactElement;
  // 数据聚合方式
  aggregation: string | React.ReactElement;
  // 更新时间
  updateTime: string | React.ReactElement;
}

/**
 * 指标气泡解释内容字段
 */
export type IMetricExplanationTipsClickKey = keyof IMetricExplanationTips;

/**
 * 指标气泡解释内容生成方法的helper配置
 */
type IMetricExplanationTipsGeneratorOption = Pick<IMetricExplanationTipsProps, 'onClick'>;

/**
 * 指标气泡解释内容生成方法
 */
type IMetricExplanationTipsGenerator = (option: IMetricExplanationTipsGeneratorOption) => IMetricExplanationTips;

/**
 * 指标气泡解释内容
 */
export type IMetricExplanationTipsData = IMetricExplanationTips | IMetricExplanationTipsGenerator;

/**
 * 指标解释气泡内容内部的click的事件名
 */
export type IMetricExplanationTipsClickEventName =
  /**
   * 点击指标气泡解释中的filter for US按钮
   */
  | 'setRegionUSOnly'
  /**
   * 点击指标气泡解释中的"查看校准原始数据"链接
   */
  | 'viewCalibrationData'
  | (string & {});

/**
 * 指标解释气泡内容内部click事件回传的载荷，包含当前触发的事件的名称（业务名称，调用者通过业务名称执行对应的处理逻辑）
 */
export interface IMetricExplanationTipsClickPayload {
  key: IMetricExplanationTipsClickEventName;
  metric?: string;
}

/**
 * 指标解释气泡内容内部的click回调
 * @param field 字段，包含definition、dataSource、granularity、aggregation、updateTime等，用来通知组件调用者当前点击的是哪一个维度的内容
 * @param payload 载荷，包含本次点击需要传递的信息，完全自定义
 * @param key 本次点击的事件名（回传给组件调用者，用于区分具体点击的是什么按钮）
 */
export type IMetricExplanationTipsClickHandler = (
  field: IMetricExplanationTipsClickKey,
  payload?: IMetricExplanationTipsClickPayload,
) => void;

/**
 * 指标气泡解释内容组件props
 */
interface IMetricExplanationTipsProps extends IBaseProps {
  // 是否展示底部的数据来源说明
  showDataSourceDescription?: boolean;
  // 实例类型
  entityType: IEntityType;
  // 数据源key
  dataSource: string;
  // 数据源图标
  dataSourceImg?: string;
  // 指标key
  metric: string;
  // 其他信息（英文）
  data: IMetricExplanationTipsData;
  // 其他信息（中文）
  dataCN: IMetricExplanationTipsData;
  // 内部点击回调
  onClick?: IMetricExplanationTipsClickHandler;
}

/**
 * 指标气泡解释内容的字段
 */
const metricExplanationKeys: IMetricExplanationTipsClickKey[] = [
  'definition',
  'granularity',
  'updateTime',
  'aggregation',
  'dataSource',
];

/**
 * 指标气泡解释字段的英文名映射
 */
const metricExplanationNamesEN: Record<string, string> = {
  definition: 'Definition',
  dataSource: 'Data Source',
  granularity: 'Time Granularity',
  updateTime: 'Update Time',
  aggregation: 'Time Period Calculation Method',
};

/**
 * 指标气泡解释字段的中文名映射
 */
const metricExplanationNamesCN: Record<string, string> = {
  definition: '指标含义',
  dataSource: '数据来源',
  granularity: '时间粒度',
  updateTime: '更新时间',
  aggregation: '时间段计算方式',
};

const generateExplanationTipsData = (
  data: IMetricExplanationTipsData,
  option: IMetricExplanationTipsGeneratorOption,
): IMetricExplanationTips => {
  if (!isFunction(data)) return data;
  return data(option);
};

const MetricExplanationTips: React.FC<IMetricExplanationTipsProps> = (props) => {
  const {
    style,
    className,
    showDataSourceDescription = false,
    entityType,
    dataSource,
    dataSourceImg,
    metric,
    data: dataProps,
    dataCN: dataCNProps,
    onClick,
  } = props;

  const data = generateExplanationTipsData(dataProps, { onClick });
  const dataCN = generateExplanationTipsData(dataCNProps, { onClick });

  const { isCN } = useIntlExtend();

  const dataLocale = (isCN ? dataCN : data) || {};
  const { dataSourceName } = data || {};
  const { dataSourceName: dataSourceNameCN } = dataCN || {};
  const dataSourceNameLocale = (isCN ? dataSourceNameCN : dataSourceName) || '';
  const dataSourceNameTmp = dataSourceNameLocale || DATASOURCE_NAME[dataSource] || dataSource || '';

  const metricExplanationNames = isCN ? metricExplanationNamesCN : metricExplanationNamesEN;

  // 处理HTML内容中的链接点击事件
  const handleContentClick = (e: React.MouseEvent) => {
    console.log('MetricExplanationTips: handleContentClick triggered', e.target);

    const target = e.target as HTMLElement;

    console.log('MetricExplanationTips: target info', {
      tagName: target.tagName,
      hasDataAction: target.hasAttribute('data-action'),
      dataAction: target.getAttribute('data-action'),
      dataMetric: target.getAttribute('data-metric'),
      onClick: !!onClick,
    });

    // 检查是否点击的是带有data-action属性的链接
    if (target.tagName === 'A' && target.hasAttribute('data-action')) {
      console.log('MetricExplanationTips: Processing link click');

      // 立即阻止默认行为和事件冒泡
      e.preventDefault();
      e.stopPropagation();
      // @ts-ignore
      e.stopImmediatePropagation();

      const action = target.getAttribute('data-action');
      const metric = target.getAttribute('data-metric');

      console.log('MetricExplanationTips: Link clicked', {
        action,
        metric,
        onClick: !!onClick,
      });

      if (action === 'viewCalibrationData' && metric && onClick) {
        console.log('MetricExplanationTips: Calling onClick callback');
        // 使用setTimeout确保事件处理完成后再执行回调
        setTimeout(() => {
          console.log('MetricExplanationTips: Executing onClick callback');
          onClick('definition', { key: 'viewCalibrationData', metric });
        }, 0);
      } else {
        console.log('MetricExplanationTips: Conditions not met', {
          actionMatch: action === 'viewCalibrationData',
          hasMetric: !!metric,
          hasOnClick: !!onClick,
        });
      }

      return false; // 确保阻止默认行为
    }
    console.log('MetricExplanationTips: Not a valid link click');
  };

  return (
    <div
      style={style}
      className={classnames('dbi-common-metric-explanation-tips dbi-common-metric-explanation-tips__list', className)}
      onClick={handleContentClick}
    >
      {metricExplanationKeys.map((metricExplanationKey) => {
        const metricExplanationName = metricExplanationNames?.[metricExplanationKey] || metricExplanationKey || '';
        const rawMetricExplanationValue = dataLocale?.[metricExplanationKey] || '';
        const metricExplanationValue = processRichText(rawMetricExplanationValue);

        if (!metricExplanationValue) {
          return null;
        }

        return (
          <div className="dbi-common-metric-explanation-tips__item" key={metricExplanationKey}>
            <span className="dbi-common-metric-explanation-tips__item-name">{metricExplanationName}: </span>
            {typeof metricExplanationValue === 'string' ? (
              <span
                className="dbi-common-metric-explanation-tips__item-value"
                dangerouslySetInnerHTML={{ __html: metricExplanationValue }}
              />
            ) : (
              <span className="dbi-common-metric-explanation-tips__item-value">{metricExplanationValue}</span>
            )}
          </div>
        );
      })}
      {showDataSourceDescription && (
        <DataSourceBubbleInfo
          isNewMore
          entityType={entityType}
          dataSource={dataSource}
          metricKey={metric}
          comesFormDom={
            <>
              <img
                style={{
                  margin: '0 4px',
                  width: '16px',
                  height: '16px',
                  verticalAlign: 'middle',
                }}
                className="dbi-common-metric-explanation-tips__icon-platform"
                src={dataSourceImg ?? getDataSourceIcon(dataSource)}
              />
              {dataSourceNameTmp}
            </>
          }
        />
      )}
    </div>
  );
};

export default MetricExplanationTips;
